import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgSelectModule } from '@ng-select/ng-select';
import { DropDownItem } from '@core/types/Dropdown.type';
import { UseCourse, UseTag } from '@core/usecases';
import { EnumToKeyValueArray } from '@core/utils';
import { CourseInstructorType, InstructorType, RoomType, TagType } from '@core/enums';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FilterQuery } from '@core/types';
import { FormsModule } from '@angular/forms';
import { RoomEntity } from '@core/entities';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { DropdownComponent, IconComponent } from '@shared/components/ui';
import { NgxBottomSheetService } from 'ngx-bottom-sheet';

@UntilDestroy()
@Component({
  selector: 'app-m-courses-sorting',
  imports: [CommonModule, IconComponent, TranslateModule, DropdownComponent, NgSelectModule, FormsModule, NgxSkeletonLoaderModule],
  templateUrl: './m-courses-sorting.component.html',
  styleUrls: ['./m-courses-sorting.component.scss'],
})
export class MCoursesSortingComponent implements OnInit {
  bottomSheetData: any = {};
  component = '';
  currentTabRoute = '';
  instructorTypeFilterItems: DropDownItem[] = [];
  roomTypeFilterItems: DropDownItem[] = [];
  courseTypeFilterItems: DropDownItem[] = [];
  tagsFilter: string[] = [];
  instructorFilters: FilterQuery = {
    tags: null,
    instructorType: null,
  };
  roomFilters: FilterQuery = {
    tags: null,
    instructorType: null,
  };
  courseFilter: FilterQuery = {
    tags: null,
    room: null,
    instructorType: null,
  };
  loadings = {
    tags: true,
    rooms: true,
    instructors: true,
  };
  roomsFilter: RoomEntity[] = [];
  private readonly _translate = inject(TranslateService);
  private readonly _bottomSheetService = inject(NgxBottomSheetService);
  private readonly _useCourse = new UseCourse();
  private readonly _useTags = new UseTag();

  constructor() {
    if (this._bottomSheetService.currentSheetData.component) {
      this.currentTabRoute = this._bottomSheetService.currentSheetData.component.toLowerCase();
    }
    if (this.currentTabRoute === 'instructors') {
      this.instructorTypeFilterItems = EnumToKeyValueArray(InstructorType, this._translate);
      this.instructorFilters = this._bottomSheetService.currentSheetData.currentFilters;
      this.instructorTypeFilterItems.unshift({
        key: this._translate.instant('All'),
        value: null,
      });
    } else if (this.currentTabRoute === 'rooms') {
      this.roomTypeFilterItems = EnumToKeyValueArray(RoomType, this._translate);
      this.roomFilters = this._bottomSheetService.currentSheetData.currentFilters;
      this.roomTypeFilterItems.unshift({
        key: this._translate.instant('All'),
        value: null,
      });
    } else if (this.currentTabRoute === 'courses') {
      this.courseTypeFilterItems = EnumToKeyValueArray(CourseInstructorType, this._translate);
      this.courseFilter = this._bottomSheetService.currentSheetData.currentFilters;
      this.courseTypeFilterItems.unshift({
        key: this._translate.instant('All'),
        value: null,
      });

      this.fetchCommonDetails();
    }

    this.loadings.instructors = false;
  }

  close() {
    this._bottomSheetService.close();
  }

  ngOnInit() {
    if (this.currentTabRoute === 'instructors') {
      this._fetchTags(TagType.INSTRUCTOR);
    } else if (this.currentTabRoute === 'rooms') {
      this._fetchTags(TagType.ROOM);
    } else if (this.currentTabRoute === 'courses') {
      this._fetchTags(TagType.COURSES);
    }
  }

  onInstructorFilterChange(value: any, filterType: 'tags' | 'instructorType') {
    if (Array.isArray(value) && value.length === 0) {
      if (this.currentTabRoute === 'instructors') {
        this.instructorFilters['tags'] = null;
      }
    } else {
      this.instructorFilters[filterType] = value;
    }
  }

  onRoomFilterChange(value: any, filterType: 'tags' | 'instructorType') {
    if (Array.isArray(value) && value.length === 0) {
      if (this.currentTabRoute === 'rooms') {
        this.roomFilters['tags'] = null;
      }
    } else {
      this.roomFilters[filterType] = value;
    }
  }

  onCourseFilterChange(value: any, filterType: 'tags' | 'room' | 'instructorType') {
    // if (Array.isArray(value) && value.length === 0) {
    //   if (this.currentTabRoute === 'courses') {
    //     this.courseFilter['tags'] = null;
    //   }
    // } else {
    //   this.courseFilter[filterType] = value;
    // }

    switch (filterType) {
      case 'tags':
        if (!value || !value.length) {
          this.courseFilter['tags'] = null;
          break;
        }
        this.courseFilter['tags'] = value;
        break;
      case 'instructorType':
        this.courseFilter['instructorType'] = value;
        break;
      case 'room':
        this.courseFilter['room'] = value;
        break;
    }
  }

  clearFilters() {
    if (this.currentTabRoute === 'instructors') {
      this.instructorFilters = {
        tags: null,
        instructorType: null,
      };
    } else if (this.currentTabRoute === 'rooms') {
      this.roomFilters = {
        tags: null,
        instructorType: null,
      };
    } else if (this.currentTabRoute === 'courses') {
      this.roomFilters = {
        tags: null,
        room: null,
        instructorType: null,
      };
    }
  }

  applyFilters() {
    if (this.currentTabRoute === 'instructors') {
      this._bottomSheetService.close(this.instructorFilters);
    } else if (this.currentTabRoute === 'rooms') {
      this._bottomSheetService.close(this.roomFilters);
    } else if (this.currentTabRoute === 'courses') {
      this._bottomSheetService.close(this.courseFilter);
    }
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // ------------------------------------------------- Private Functions -------------------------------------------------
  // ---------------------------------------------------------------------------------------------------------------------

  fetchCommonDetails(): void {
    this._useCourse
      .getCoursesCommonInfo()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (res) => {
          this.roomsFilter = res.rooms;
          this.loadings.rooms = false;
        },
      });
  }

  private _fetchTags(tagType: TagType) {
    this._useTags
      .getAllTagsByType(tagType)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (res) => {
          if (res && res.length) {
            this.tagsFilter = res.map((tag) => tag.tagName);
            this.loadings.tags = false;
          }
        },
      });
  }
}
